import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout.jsx';
import './MyProjects.css';
import { projectService } from '../services/apiService.jsx';
import { useAuth } from '../context/AuthContext.jsx';

const canManageProject = (role) => {
  const normalized = String(role || '').toLowerCase().trim().replace(/[_-]+/g, ' ');
  return ['admin', 'super admin', 'hod', 'head of department', 'team lead', 'team leader', 'tl'].includes(normalized);
};
const isEmployee = (role) => String(role || '').toLowerCase().trim() === 'employee';

// Project Details Modal Component
const ProjectDetailsModal = ({ project, onClose }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return isNaN(d) ? '-' : d.toLocaleDateString();
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header bg-light border-bottom">
            <h5 className="modal-title">Project Details</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <div className="card">
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                      <strong>Project Name:</strong>
                    </p>
                    <p style={{ fontSize: '1rem' }}>{project.project_name || '-'}</p>
                  </div>
                  <div className="col-md-6">
                    <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                      <strong>Status:</strong>
                    </p>
                    <p style={{ fontSize: '1rem' }}>
                      <span className={`badge bg-${project.project_status?.toLowerCase() === 'active' ? 'success' : project.project_status?.toLowerCase() === 'inactive' ? 'danger' : 'secondary'}`}>
                        {project.project_status || 'Billable'}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                      <strong>Project Manager:</strong>
                    </p>
                    <p style={{ fontSize: '1rem' }}>{project.project_manager_name || '-'}</p>
                  </div>
                  <div className="col-md-6">
                    <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                      <strong>Project Type:</strong>
                    </p>
                    <p style={{ fontSize: '1rem' }}>{project.project_type || '-'}</p>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                      <strong>Customer Name:</strong>
                    </p>
                    <p style={{ fontSize: '1rem' }}>{project.customer_name || '-'}</p>
                  </div>
                  <div className="col-md-6">
                    <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                      <strong>Employee Name:</strong>
                    </p>
                    <p style={{ fontSize: '1rem' }}>{project.employee_name || '-'}</p>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                      <strong>Start Date:</strong>
                    </p>
                    <p style={{ fontSize: '1rem' }}>{formatDate(project.project_start_date)}</p>
                  </div>
                  <div className="col-md-6">
                    <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                      <strong>End Date:</strong>
                    </p>
                    <p style={{ fontSize: '1rem' }}>{formatDate(project.project_end_date)}</p>
                  </div>
                </div>

                {project.project_description && (
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                        <strong>Description:</strong>
                      </p>
                      <p style={{ fontSize: '1rem' }}>{project.project_description}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectTable = ({ projects, onDelete, user, loading, emptyIcon, emptyText, onView }) => {
  const navigate = useNavigate();

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return isNaN(d) ? '-' : d.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Loading projects...</p>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="text-center py-5">
        <i className={`bi ${emptyIcon}`} style={{ fontSize: '3rem', color: 'var(--gray-300)' }}></i>
        <p className="mt-2" style={{ color: 'var(--gray-600)' }}>{emptyText}</p>
      </div>
    );
  }

  const canEdit = canManageProject(user?.role);
  const showActions = !isEmployee(user?.role);

  return (
    <div className="table-responsive my-projects-table-wrap">
      <table className={`table table-hover align-middle mb-0 grouped-project-table ${showActions ? 'has-actions' : 'no-actions'}`}>
        <colgroup>
          <col className="project-code-col" />
          <col className="project-name-col" />
          <col className="project-client-col" />
          <col className="project-location-col" />
          <col className="project-manager-col" />
          <col className="project-date-col" />
          <col className="project-status-col" />
          {showActions && <col className="project-actions-col" />}
        </colgroup>
        <thead>
          <tr>
            <th>Code</th>
            <th>Project Name</th>
            <th>Client</th>
            <th>Location</th>
            <th>Project Manager</th>
            <th>Start Date</th>
            <th>Status</th>
            {showActions && <th className="text-center">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => (
            <tr key={p.project_id || p.id}>
              <td><span className="my-project-code">{p.project_code || '-'}</span></td>
              <td className="project-name-cell"><strong>{p.project_name}</strong></td>
              <td>{p.customer_name || '-'}</td>
              <td>{p.client_location || '-'}</td>
              <td>{p.project_manager_name || '-'}</td>
              <td>{formatDate(p.project_start_date)}</td>
              <td>
                <span className={`status-badge ${p.project_status?.toLowerCase()}`}>
                  {p.project_status || 'Billable'}
                </span>
              </td>
              {showActions && (
                <td>
                  <div className="project-row-actions">
                    <button
                      onClick={() => onView(p)}
                      className="btn btn-sm btn-info"
                      title="View Project Details"
                      aria-label={`View ${p.project_name}`}
                    >
                      <i className="bi bi-eye"></i>
                    </button>
                    {canEdit && <>
                      <button
                        onClick={() => navigate(`/update-project/${p.project_id}`)}
                        className="btn btn-sm btn-outline-primary"
                        title="Edit Project"
                        aria-label={`Edit ${p.project_name}`}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to delete project "${p.project_name}"? This action cannot be undone.`))
                            onDelete(p.project_id);
                        }}
                        className="btn btn-sm btn-outline-danger"
                        title="Delete Project"
                        aria-label={`Delete ${p.project_name}`}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </>}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ProjectCard = ({ project, onDelete, user, onView }) => {
  const navigate = useNavigate();
  const {
    project_id,
    project_name,
    project_manager_name,
    project_start_date,
    project_status
  } = project;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return isNaN(d) ? '-' : d.toLocaleDateString();
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    navigate(`/update-project/${project_id}`);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete project "${project_name}"? This action cannot be undone.`)) {
      onDelete(project_id);
    }
  };

  const handleView = (e) => {
    e.stopPropagation();
    onView(project);
  };

  return (
    <div className="col-xl-4 col-lg-6 col-md-6 mb-4">
      <div className="project-card">
        <div className="project-card-header">
          <h5 className="project-title">{project_name}</h5>
          <div className="d-flex align-items-center gap-2">
            <span className={`status-badge ${project_status?.toLowerCase()}`}>
              {project_status || "Billable"}
            </span>
            {!isEmployee(user?.role) && (
              <>
                <button
                  onClick={handleView}
                  className="btn btn-sm btn-info"
                  title="View Project Details"
                >
                  <i className="bi bi-eye"></i>
                </button>
                {canManageProject(user?.role) && <>
                  <button
                    onClick={handleEdit}
                    className="btn btn-sm btn-outline-primary"
                    title="Edit Project"
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button
                    onClick={handleDelete}
                    className="btn btn-sm btn-outline-danger"
                    title="Delete Project"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </>}
              </>
            )}
          </div>
        </div>

        <div className="project-body">
          <div className="info-row">
            <div>
              <label>Project Manager</label>
              <p>{project_manager_name || "-"}</p>
            </div>
          </div>

          <div className="info-row">
            <div>
              <label>Start Date</label>
              <p>{formatDate(project_start_date)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MyProjects = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    document.title = 'My Projects';
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const res = await projectService.getAllProjects();
      const projectRows = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
      setProjects(projectRows);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
  };

  const handleCloseModal = () => {
    setSelectedProject(null);
  };

  const handleDeleteProject = async (projectId) => {
    setDeletingId(projectId);
    try {
      await projectService.deleteProject(projectId);
      await loadProjects();
      alert('Project deleted successfully!');
    } catch (err) {
      console.error('Error deleting project:', err);
      let errorMsg = 'Failed to delete project';
      if (err.response) {
        if (err.response.status === 401) errorMsg = 'You are not authorized. Please login again.';
        else if (err.response.status === 403) errorMsg = "You don't have permission to delete projects.";
        else if (err.response.status === 404) errorMsg = 'Project not found.';
        else errorMsg = err.response.data?.message || errorMsg;
      } else if (err.request) {
        errorMsg = 'No response from server. Please check your internet connection.';
      }
      alert(errorMsg);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreateProject = () => {
    navigate('/createproject');
  };

  const currentProjects = projects.filter(p =>
    p.project_status?.toLowerCase() === 'active' ||
    p.project_status?.toLowerCase() === 'ongoing' ||
    !p.project_status
  );

  const upcomingProjects = projects.filter(p =>
    p.project_status?.toLowerCase() === 'upcoming'
  );

  const completedProjects = projects.filter(p =>
    p.project_status?.toLowerCase() === 'completed' ||
    p.project_status?.toLowerCase() === 'closed'
  );

  return (
    <Layout>
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col-md-6">
            <div className="pagetitle">
              <h1>My Projects</h1>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex justify-content-end align-items-center gap-2">
              <div className="btn-group" role="group">
                <button
                  className={`btn btn-sm ${viewMode === 'table' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('table')}
                  title="Table View"
                  style={{ padding: '2px 7px', fontSize: '0.75rem' }}
                >
                  <i className="bi bi-table"></i>
                </button>
                <button
                  className={`btn btn-sm ${viewMode === 'card' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('card')}
                  title="Card View"
                  style={{ padding: '2px 5px', fontSize: '0.75rem' }}
                >
                  <i className="bi bi-grid-3x3-gap"></i>
                </button>
              </div>
              {!isEmployee(user?.role) && (
                <button
                  className="btn btn-success d-flex align-items-center"
                  onClick={handleCreateProject}
                >
                  <i className="bi bi-plus"></i>
                  Create Project
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="card">
          <div className="card-body">
            {/* Tabs */}
            <ul className="nav nav-tabs modern-tabs" role="tablist">
              <li className="nav-item">
                <button
                  className="nav-link active"
                  id="current-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#current"
                  type="button"
                  role="tab"
                >
                  Current Projects <span className="badge bg-secondary ms-1">{currentProjects.length}</span>
                </button>
              </li>
              <li className="nav-item">
                <button
                  className="nav-link"
                  id="upcoming-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#upcoming"
                  type="button"
                  role="tab"
                >
                  Upcoming <span className="badge bg-secondary ms-1">{upcomingProjects.length}</span>
                </button>
              </li>
              <li className="nav-item">
                <button
                  className="nav-link"
                  id="completed-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#completed"
                  type="button"
                  role="tab"
                >
                  Completed <span className="badge bg-secondary ms-1">{completedProjects.length}</span>
                </button>
              </li>
            </ul>

            <div className="tab-content">
              {/* Current Projects Tab */}
              <div className="tab-pane fade show active" id="current" role="tabpanel">
                {viewMode === 'table' ? (
                  <ProjectTable
                    projects={currentProjects}
                    onDelete={handleDeleteProject}
                    user={user}
                    loading={loading}
                    emptyIcon="bi-folder-x"
                    emptyText="No current projects found."
                    onView={handleViewProject}
                  />
                ) : (
                  <div className="row g-4">
                    {loading ? (
                      <div className="col-12 text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading projects...</p>
                      </div>
                    ) : currentProjects.length === 0 ? (
                      <div className="col-12 text-center py-5">
                        <i className="bi bi-folder-x" style={{ fontSize: '3rem', color: 'var(--gray-300)' }}></i>
                        <p className="mt-2" style={{ color: 'var(--gray-600)' }}>No current projects found.</p>
                      </div>
                    ) : (
                      currentProjects.map((p) => (
                        <ProjectCard key={p.project_id || p.id} project={p} onDelete={handleDeleteProject} user={user} onView={handleViewProject} />
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Upcoming Projects Tab */}
              <div className="tab-pane fade" id="upcoming" role="tabpanel">
                {viewMode === 'table' ? (
                  <ProjectTable
                    projects={upcomingProjects}
                    onDelete={handleDeleteProject}
                    user={user}
                    loading={loading}
                    emptyIcon="bi-calendar-x"
                    emptyText="No upcoming projects found."
                    onView={handleViewProject}
                  />
                ) : (
                  <div className="row g-4">
                    {loading ? (
                      <div className="col-12 text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading projects...</p>
                      </div>
                    ) : upcomingProjects.length === 0 ? (
                      <div className="col-12 text-center py-5">
                        <i className="bi bi-calendar-x" style={{ fontSize: '3rem', color: 'var(--gray-300)' }}></i>
                        <p className="mt-2" style={{ color: 'var(--gray-600)' }}>No upcoming projects found.</p>
                      </div>
                    ) : (
                      upcomingProjects.map((p) => (
                        <ProjectCard key={p.project_id || p.id} project={p} onDelete={handleDeleteProject} user={user} onView={handleViewProject} />
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Completed Projects Tab */}
              <div className="tab-pane fade" id="completed" role="tabpanel">
                {viewMode === 'table' ? (
                  <ProjectTable
                    projects={completedProjects}
                    onDelete={handleDeleteProject}
                    user={user}
                    loading={loading}
                    emptyIcon="bi-check2-circle"
                    emptyText="No completed projects found."
                    onView={handleViewProject}
                  />
                ) : (
                  <div className="row g-4">
                    {loading ? (
                      <div className="col-12 text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading projects...</p>
                      </div>
                    ) : completedProjects.length === 0 ? (
                      <div className="col-12 text-center py-5">
                        <i className="bi bi-check2-circle" style={{ fontSize: '3rem', color: 'var(--gray-300)' }}></i>
                        <p className="mt-2" style={{ color: 'var(--gray-600)' }}>No completed projects found.</p>
                      </div>
                    ) : (
                      completedProjects.map((p) => (
                        <ProjectCard key={p.project_id || p.id} project={p} onDelete={handleDeleteProject} user={user} onView={handleViewProject} />
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {selectedProject && <ProjectDetailsModal project={selectedProject} onClose={handleCloseModal} />}
    </Layout>
  );
};

export default MyProjects;
